import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { BRAND, SERVICE_CATEGORIES, SUB_BRANDS } from "../../data/content";
import { trackEvent } from "../../lib/analytics";
import BrandLogo from "../BrandLogo";

const navLink = "text-sm font-medium text-white/80 hover:text-white transition-colors gs-link-underline";
const navActive = "text-white";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const megaRef = useRef(null);
  const location = useLocation();

  // Close mega menu on route change
  useEffect(() => { setMegaOpen(false); setOpen(false); }, [location.pathname]);

  // Close on outside click
  useEffect(() => {
    if (!megaOpen) return;
    const handler = (e) => {
      if (megaRef.current && !megaRef.current.contains(e.target)) setMegaOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [megaOpen]);

  // Close on Escape
  useEffect(() => {
    if (!megaOpen) return;
    const esc = (e) => e.key === "Escape" && setMegaOpen(false);
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [megaOpen]);

  return (
    <header className="fixed top-0 inset-x-0 z-50 gs-glass border-b border-white/10" data-testid="site-navbar">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center" data-testid="nav-home-logo">
            <BrandLogo variant="full-white" className="h-10 lg:h-12 w-auto block" priority />
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            <NavLink to="/" end className={({isActive}) => `${navLink} ${isActive ? navActive : ""}`} data-testid="nav-home">Home</NavLink>
            <NavLink to="/about" className={({isActive}) => `${navLink} ${isActive ? navActive : ""}`} data-testid="nav-about">About</NavLink>

            <div className="relative" ref={megaRef}>
              <button
                onClick={() => setMegaOpen(v => !v)}
                aria-expanded={megaOpen}
                aria-haspopup="true"
                className={`${navLink} flex items-center gap-1 ${megaOpen ? "text-white" : ""}`}
                data-testid="nav-services-trigger"
              >
                Services <ChevronDown className={`w-3.5 h-3.5 transition-transform ${megaOpen ? "rotate-180" : ""}`} />
              </button>
              {megaOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[860px] bg-[#0A0F1C] border border-white/10 shadow-2xl grid grid-cols-[1.2fr_1fr] gap-0" data-testid="mega-services">
                  <div className="p-8 grid grid-cols-1 gap-4 border-r border-white/10">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Our Services</p>
                    {SERVICE_CATEGORIES.map((s) => (
                      <Link key={s.slug} to={`/services/${s.slug}`} className="group block" data-testid={`mega-service-${s.slug}`}>
                        <div className="flex items-start justify-between py-2 border-b border-white/5">
                          <div>
                            <p className="text-white font-semibold group-hover:gs-gradient-text transition-all">{s.name}</p>
                            <p className="text-xs text-white/50 mt-0.5">{s.legislation}</p>
                          </div>
                          <span className="text-white/30 group-hover:text-white transition">→</span>
                        </div>
                      </Link>
                    ))}
                  </div>
                  <div className="p-8 bg-black/40">
                    <p className="text-[10px] uppercase tracking-[0.25em] text-white/40">Trading Brands</p>
                    <div className="mt-4 space-y-3">
                      {Object.values(SUB_BRANDS).map((b) => (
                        <a key={b.slug} href={b.website} target="_blank" rel="noreferrer" className="block p-4 border border-white/10 hover:border-white/30 transition-all" data-testid={`mega-brand-${b.slug}`}>
                          <p className="text-white font-semibold">{b.name}</p>
                          <p className="text-xs text-white/50 mt-1">{b.tagline}</p>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <NavLink to="/sectors" className={({isActive}) => `${navLink} ${isActive ? navActive : ""}`} data-testid="nav-sectors">Sectors</NavLink>
            <NavLink to="/coverage" className={({isActive}) => `${navLink} ${isActive ? navActive : ""}`} data-testid="nav-coverage">Coverage</NavLink>
            <NavLink to="/projects" className={({isActive}) => `${navLink} ${isActive ? navActive : ""}`} data-testid="nav-projects">Projects</NavLink>
            <NavLink to="/compliance-hub" className={({isActive}) => `${navLink} ${isActive ? navActive : ""}`} data-testid="nav-hub">Compliance Hub</NavLink>
            <NavLink to="/blog" className={({isActive}) => `${navLink} ${isActive ? navActive : ""}`} data-testid="nav-blog">Blog</NavLink>
            <NavLink to="/accreditations" className={({isActive}) => `${navLink} ${isActive ? navActive : ""}`} data-testid="nav-accreditations">Accreditations</NavLink>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a href={`tel:${BRAND.phone.replace(/\s/g,"")}`} onClick={() => trackEvent("contact_phone", { location: "navbar" })} className="flex items-center gap-2 text-sm text-white/80 hover:text-white" data-testid="nav-phone">
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
            <Link to="/contact" onClick={() => trackEvent("request_quote_click", { location: "navbar" })} className="gs-gradient text-white font-semibold px-5 py-2.5 text-sm hover:opacity-90 transition-opacity shadow-lg shadow-[#3FBAB0]/20" data-testid="nav-quote">
              Get a Free Quote
            </Link>
          </div>

          <button className="lg:hidden text-white" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="nav-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden pb-6 pt-2 space-y-3 border-t border-white/10" data-testid="nav-mobile-menu">
            {["/", "/about", "/services", "/sectors", "/coverage", "/projects", "/compliance-hub", "/blog", "/accreditations", "/contact"].map((p, i) => (
              <Link key={p} to={p} className="block text-white/80 hover:text-white py-1" onClick={() => setOpen(false)} data-testid={`nav-mobile-link-${i}`}>
                {p === "/" ? "Home" : p.replace("/", "").replace("-", " ").replace(/\b\w/g, c => c.toUpperCase())}
              </Link>
            ))}
            <Link to="/contact" onClick={() => { setOpen(false); trackEvent("request_quote_click", { location: "navbar_mobile" }); }} className="block gs-gradient text-white font-semibold px-5 py-3 text-center" data-testid="nav-mobile-quote">
              Get a Free Quote
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}
